import styled from 'styled-components';

import { media } from 'src/styles/breakpoints';

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  ${media.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const StatCard = styled.div<{ $tone?: 'positive' | 'negative' | 'muted' }>`
  padding: 0.9rem 1rem;
  border-radius: 0.75rem;
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.grey};
  border-left: 4px solid
    ${({ theme, $tone }) =>
      $tone === 'positive' ? theme.positive || '#16a34a' : $tone === 'negative' ? theme.negative || '#dc2626' : theme.grey};
`;

export const StatLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${({ theme }) => theme.midGrey};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0.3rem;
`;

export const StatValue = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.dark};
`;

export const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $status }) =>
    ['COMPLETED', 'DELIVERED', 'SENT'].includes($status)
      ? '#166534'
      : ['FAILED', 'CANCELED'].includes($status)
        ? '#991b1b'
        : ['PREPARING', 'PROCESSING', 'QUEUED'].includes($status)
          ? '#1d4ed8'
          : '#374151'};
  background: ${({ $status }) =>
    ['COMPLETED', 'DELIVERED', 'SENT'].includes($status)
      ? '#dcfce7'
      : ['FAILED', 'CANCELED'].includes($status)
        ? '#fee2e2'
        : ['PREPARING', 'PROCESSING', 'QUEUED'].includes($status)
          ? '#dbeafe'
          : '#f3f4f6'};
`;

export const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  & > div {
    margin-bottom: 0;
  }
`;

export const FieldLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.midGrey};
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

export const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 5.5rem;
  resize: vertical;
  padding: 0.75rem 0.9rem;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.dark};
  font: inherit;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary || '#007bff'};
  }
`;

export const FieldFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.dark};
  cursor: pointer;
`;

export const RadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const RadioChip = styled.button<{ $active: boolean }>`
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.dark : theme.grey)};
  background: ${({ theme, $active }) => ($active ? theme.dark : theme.background)};
  color: ${({ theme, $active }) => ($active ? theme.background : theme.dark)};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
`;

export const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.4rem 0.3rem 0.65rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.lightGrey};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.dark};

  button {
    border: 0;
    background: transparent;
    cursor: pointer;
    font-size: 1rem;
    line-height: 1;
    color: ${({ theme }) => theme.midGrey};
  }
`;

export const OptionList = styled.div`
  max-height: 12rem;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.background};
`;

export const OptionRow = styled.button<{ $selected?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.55rem 0.8rem;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.lightGrey};
  background: ${({ theme, $selected }) => ($selected ? theme.lightGrey : 'transparent')};
  color: ${({ theme }) => theme.dark};
  font-size: 0.85rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.lightGrey};
  }

  small {
    color: ${({ theme }) => theme.midGrey};
  }
`;

export const Callout = styled.p<{ $tone?: 'warning' | 'info' }>`
  margin: 0;
  padding: 0.75rem 0.9rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme, $tone }) => ($tone === 'warning' ? theme.middle : theme.grey)};
  background: ${({ theme }) => theme.lightGrey};
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.dark};
`;

export const MetaLine = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.midGrey};
`;

export const ErrorText = styled.span`
  color: #991b1b;
  font-size: 0.8rem;
`;

export const ActionRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

export const Muted = styled.span`
  color: ${({ theme }) => theme.midGrey};
`;

export const CharCount = styled.span<{ $over: boolean }>`
  margin-left: auto;
  color: ${({ theme, $over }) => ($over ? theme.negative || '#dc2626' : theme.midGrey)};
`;

export const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;

  strong {
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  span {
    font-size: 0.75rem;
  }
`;
